import { definedMap } from "@glideapps/ts-necessities";
import type { Currency } from "common/components/buy-button-defaults";
import { defaultCurrency, findCurrency } from "common/components/buy-button-defaults";
import {
    asArrayValue,
    asBoolean,
    asNumber,
    asString,
    isLoadedDefined,
    loadedDefinedMap,
    loadedDefinedMapWithDefault,
} from "common/components/component-helpers";
import { massageImageUrl } from "common/components/portable-renderers";
import type { PotentialValue } from "common/components/types";
import { ActionSource, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { logError } from "common/debug-print";
import { isBaseRowIndex } from "common/firebase-function-types";
import { definedMapWithDefault, mapFilterUndefined } from "common/Support";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import type { BuyScreenTransactionBootstrap, BuyScreenTransactionItem } from "../lib/buy-screen";
import BuyScreen from "../ui/smart/buy-screen/buy-screen";

type ExtendedBuyScreenTransactionItem = BuyScreenTransactionItem &
    Readonly<{
        currency: Currency;
        withShipping: boolean;
        showStripeLogo: boolean;
    }>;

function makeBuyScreenTransactionItem(
    itemButtonID: PotentialValue,
    itemName: PotentialValue,
    itemDescription: PotentialValue,
    itemImage: PotentialValue,
    itemSKU: PotentialValue,
    itemPrice: PotentialValue,
    rowIndexValue: PotentialValue,
    currencyCode: PotentialValue,
    withShipping: PotentialValue,
    showStripeLogo: PotentialValue,
    appID: string
): ExtendedBuyScreenTransactionItem | undefined {
    if (
        !isLoadedDefined(itemName) ||
        !isLoadedDefined(itemSKU) ||
        !isLoadedDefined(itemPrice) ||
        !isLoadedDefined(itemButtonID) ||
        rowIndexValue === LOADING ||
        currencyCode === LOADING ||
        (rowIndexValue !== undefined && !isBaseRowIndex(rowIndexValue))
    ) {
        return undefined;
    }

    currencyCode = definedMapWithDefault(currencyCode, defaultCurrency.code, asString);
    const currency = findCurrency(currencyCode);
    if (currency === undefined) {
        logError("Currency not found", currencyCode);
        return undefined;
    }

    return {
        buttonID: asString(itemButtonID),
        name: asString(itemName),
        description: loadedDefinedMapWithDefault(itemDescription, "", asString),
        image: loadedDefinedMap(itemImage, i => massageImageUrl(asString(i), { thumbnail: true }, appID)),
        sku: asString(itemSKU),
        price: asNumber(itemPrice),
        rowIndex: rowIndexValue,
        currency,
        withShipping: loadedDefinedMapWithDefault(withShipping, true, asBoolean),
        showStripeLogo: loadedDefinedMapWithDefault(showStripeLogo, false, asBoolean),
    };
}
export const BuyScreenMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [
            itemName,
            itemDescription,
            itemImage,
            itemSKU,
            itemPrice,
            currencyCodeValue,
            withShippingValue,
            buttonIDValue,
            onFinished,
            rowIndexValue,
            itemsValue,
            showStripeLogoValue,
        ],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const { onClick: onFinishCB } = useOnClickFromAction(onFinished, ActionSource.BuyScreen, ctx, env);

    let items: ExtendedBuyScreenTransactionItem[];

    const { paymentInformation, appID } = env.appEnvironment;

    if (isLoadedDefined(itemsValue)) {
        items = mapFilterUndefined(asArrayValue(itemsValue), vp => {
            const i = vp.getObjectInspector(force);
            if (!isLoadedDefined(i)) return undefined;
            return makeBuyScreenTransactionItem(
                definedMap(i.getProperty("buttonID"), force),
                definedMap(i.getProperty("itemName"), force),
                definedMap(i.getProperty("itemDescription"), force),
                definedMap(i.getProperty("itemImage"), force),
                definedMap(i.getProperty("itemSKU"), force),
                definedMap(i.getProperty("itemPrice"), force),
                definedMap(i.getProperty("rowIndex"), force),
                definedMap(i.getProperty("currencyCode"), force),
                definedMap(i.getProperty("withShipping"), force),
                definedMap(i.getProperty("showStripeLogo"), force),
                appID
            );
        });
    } else if (itemsValue === LOADING) {
        items = [];
    } else {
        const maybeItem = makeBuyScreenTransactionItem(
            buttonIDValue,
            itemName,
            itemDescription,
            itemImage,
            itemSKU,
            itemPrice,
            rowIndexValue,
            currencyCodeValue,
            withShippingValue,
            showStripeLogoValue,
            appID
        );
        if (maybeItem === undefined) {
            items = [];
        } else {
            items = [maybeItem];
        }
    }

    const [item] = items;
    if (item === undefined) return null;

    const forThisButton = paymentInformation[item.buttonID]; // We assume all items share the same payment information.
    const withShipping = items.find(i => i.withShipping) !== undefined;
    const showStripeLogo = items.find(i => i.showStripeLogo) !== undefined;
    if (forThisButton === undefined || forThisButton.processor !== "stripe") return null;

    const basis: BuyScreenTransactionBootstrap = {
        appID,
        currency: item.currency, // We assume all items share the same currency.
        withShipping,
        items,
        showStripeLogo,
    };

    return (
        <BuyScreen
            basis={basis}
            stripePaymentInformation={forThisButton}
            appEnvironment={env.appEnvironment}
            onFinished={onFinishCB !== undefined ? () => onFinishCB(undefined) : undefined}
        />
    );
};
